import './MailReminderExecutor.scss';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IMailReminderInfo } from '@models/document/actionsExecution/IMailReminderModel';
import { DocumentExecutionService } from '@services/actions/DocumentExecutionService';
import { useForm } from 'react-hook-form';
import { RemindersService } from '@services/RemindersService';
import ModalContainer, { IModalContainerProps } from '@molecules/ModalContainer/ModalContainer';
import DateTimeFieldControl from '@srm/ui/controls/DateTimeFieldControl';
import TextareaControl from '@srm/ui/controls/TextareaControl';
import { Divider } from '@mantine/core';
import RemindersList from './RemindersList';
import { IMailReminderExecutorProps } from './IMailReminderExecutorProps';
import { IMailReminderFormData } from './IMailReminderFormData';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';

export const MailReminderExecutorModal: React.FC<IMailReminderExecutorProps> = (props: IMailReminderExecutorProps) => {
    const [errorText, setErrorText] = useState<string>();
    // Подготовка (список напоминаний) и запись (создание, удаление) — разные состояния:
    // подготовку можно бросить закрытием окна, запись — нет.
    const [loading, setLoading] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [pendingDeletes, setPendingDeletes] = useState<number>(0);
    const [existingReminders, setExistingReminders] = useState<IMailReminderInfo[]>([]);
    const service = new DocumentExecutionService(props.docId);
    const initialDeliveryDateTime = useRef(new Date()).current;
    const formState = useForm<IMailReminderFormData>({
        mode: 'onBlur', // "onChange"
        defaultValues: {
            message: props.prefilledText ?? '',
            deliveryDateTime: initialDeliveryDateTime,
        },
    });
    const hasUnsavedInput = useUnsavedFormInput(formState, { textFields: ['message'] });

    useEffect(() => {
        // Закрытое окно ответ подготовки не применяет.
        let ignore = false;
        setLoading(true);
        service
            .getMailReminderModel(props.actionKey)
            .then((res) => {
                if (!ignore) setExistingReminders(res.data.existingReminders);
            })
            .catch((error) => {
                if (!ignore) setErrorText(error);
            })
            .finally(() => {
                if (!ignore) setLoading(false);
            });
        return () => {
            ignore = true;
        };
    }, []);

    const onSubmit = (form: IMailReminderFormData) => {
        if (!form.deliveryDateTime || Number.isNaN(form.deliveryDateTime.getTime())) {
            setErrorText('Не указано время доставки напоминания');
            return;
        }

        setIsSubmitting(true);

        return service
            .createMailReminder(props.actionKey, form.deliveryDateTime, form.message)
            .then(() => {
                props.completeHandler && props.completeHandler(true);
            })
            .catch((err) => {
                setErrorText(err);
            })
            .finally(() => setIsSubmitting(false));
    };

    const onDeleteReminder = useCallback((reminder: IMailReminderInfo) => {
        setPendingDeletes((count) => count + 1);
        RemindersService.removeReminder(reminder.id)
            .then(() => {
                setExistingReminders((current) => current.filter((r) => r.id !== reminder.id));
            })
            .catch((err) => {
                setErrorText(err);
            })
            .finally(() => setPendingDeletes((count) => count - 1));
    }, []);

    const modalContainerProps: IModalContainerProps = {
        header: props.title,
        size: props.modalSize,
        showOkButton: true,
        showCancelButton: true,
        okButtonText: props.okButtonText,
        cancelButtonText: props.cancelButtonText,
        loading: loading || isSubmitting,
        closeDisabled: isSubmitting || pendingDeletes > 0,
        hasUnsavedInput,
        onCancel: () => {
            props.completeHandler && props.completeHandler(false);
        },
        errorText: errorText,
        formMethods: formState,
        onSubmit: onSubmit,
        okButtonDisabled: false,
    };

    return (
        <ModalContainer {...modalContainerProps}>
            <TextareaControl
                label="Текст напоминания"
                readOnly={false}
                required={false}
                name="message"
                autoResize={true}
                defaultValue={props.prefilledText}
                control={formState.control}
            />
            <div className={'MailReminder__DeliveryDateTimeBlock'}>
                <DateTimeFieldControl
                    label="Время доставки напоминания"
                    readOnly={false}
                    required={true}
                    name="deliveryDateTime"
                    rules={{
                        required: 'Не указано время доставки напоминания',
                        validate: {
                            isValidDate: (value) =>
                                (value instanceof Date && !Number.isNaN(value.getTime())) ||
                                'Не указано время доставки напоминания',
                        },
                    }}
                    control={formState.control}
                    minDate={initialDeliveryDateTime}
                    defaultValue={initialDeliveryDateTime}
                />
            </div>
            <div className={'MailReminder__DelimiterBlock'}>
                <Divider />
            </div>
            <div className={'MailReminder__ExistingRemindersBlock'}>
                <div className="MailReminder__SectionTitle">
                    Назначенные напоминания
                    {existingReminders.length > 0 && <span>{existingReminders.length}</span>}
                </div>
                <RemindersList reminders={existingReminders} onDelete={onDeleteReminder} />
            </div>
        </ModalContainer>
    );
};
